import { Avatar, Badge, Box, CircularProgress, Divider, List, ListItem, ListItemAvatar, ListItemText, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { Chat, User } from './type';
import api from './api';
import { CURRENT_USER } from './mock';

interface UserListProps {
    handleUserSelect: (user: User) => void;
    chats: Chat[];
}

export default function UserList({ handleUserSelect, chats }: UserListProps) {
    const [availableUsers, setAvailableUsers] = React.useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = React.useState<boolean>(false);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    useEffect(() => {
        setLoadingUsers(true);
        const fetchChats = async () => {
            try {
                const users = await api.getAvailableUsers();
                // Filter out users who already have active chats and current user
                const existingChatUserIds = chats.map(c => c.participant.id);
                const filteredUsers = users.filter(u => !existingChatUserIds.includes(u.id) && u.id !== CURRENT_USER.id);
                setAvailableUsers(filteredUsers);
            } catch (e) {
                console.error("Failed to fetch users", e);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchChats();
    }, [])

    const filteredUsers = useMemo(() => {
        if (!searchTerm.trim()) return availableUsers;
        return availableUsers.filter(user =>
            user?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
        );
    }, [availableUsers, searchTerm]);

    return <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Search Field */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <TextField
                fullWidth
                size="small"
                placeholder="Buscar usuário..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        </Box>

        {/* User List with Scroll */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List disablePadding>
                {loadingUsers ? <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress size={32} />
                </Box> : filteredUsers.length === 0 ? <Box p={4} textAlign="center" color="text.secondary">
                    <Typography variant="body2">
                        {searchTerm ? 'Nenhum usuário encontrado.' : 'Sem usuários disponíveis para nova conversa.'}
                    </Typography>
                </Box> : filteredUsers.map((user) => (
                    <React.Fragment key={user.id}>
                        <ListItem
                            button
                            onClick={() => handleUserSelect(user)}
                            alignItems="center"
                        >
                            <ListItemAvatar>
                                <Badge
                                    color="primary"
                                    variant="dot"
                                    invisible={!user.is_online}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    overlap="circular"
                                >
                                    <Avatar src={user.avatar_url}>
                                        {user.name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                </Badge>
                            </ListItemAvatar>
                            <ListItemText
                                primary={<Typography variant="subtitle2" style={{ fontWeight: 600 }}>{user.name}</Typography>}
                                secondary={
                                    <Typography variant="caption" color="textSecondary">
                                        {user.is_online ? 'Disponível' : 'Offline'}
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <Divider component="li" />
                    </React.Fragment>
                ))}
            </List>
        </Box>
    </Box>
}